/**
 * Excel Roundtrip Service
 * Handles the "export -> edit -> upload -> apply" batch editing flow via Excel.
 *
 * Backend contract (field mapping is centralized in
 * adaptParseResponse / adaptApplyResponse so it can be adjusted in one place):
 * - POST /api/excel-roundtrip/export { entity_type, ids } -> xlsx blob
 * - POST /api/excel-roundtrip/parse  (multipart: file + entity_type) -> { documents: [...], changes, ... }
 * - POST /api/excel-roundtrip/apply  { entity_type, changes } -> { applied: [...], failed: [...], skipped: [...] }
 */

import httpClient from './httpClient';
import excelExportService from './excelExportService';

export const VALID_ENTITY_TYPES = ['purchase_order', 'quotation', 'outbound'];

class ExcelRoundtripService {
  constructor() {
    this.baseURL = '/api/excel-roundtrip';
  }

  /**
   * Export documents as an editable xlsx file
   * @param {string} entityType - 'purchase_order' | 'quotation' | 'outbound'
   * @param {Array<string>} ids - Document ids to export
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async exportRoundtrip(entityType, ids) {
    this._validateEntityType(entityType);
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error('请先选择要导出的单据');
    }

    try {
      const response = await httpClient.post(
        `${this.baseURL}/export`,
        { entity_type: entityType, ids },
        { responseType: 'blob', timeout: 60000 }
      );

      return {
        blob: response.data,
        filename: this._extractFilename(response, entityType),
      };
    } catch (error) {
      throw this._handleError(error, '导出失败');
    }
  }

  /**
   * Download the exported file blob (delegates to excelExportService)
   * @param {Blob} blob
   * @param {string} filename
   */
  downloadBlob(blob, filename) {
    excelExportService.downloadBlob(blob, filename);
  }

  /**
   * Upload an edited xlsx and parse the changes
   * @param {string} entityType
   * @param {File} file
   * @returns {Promise<{documents: Array, errors: Array<string>}>} Normalized parse result
   */
  async parseRoundtrip(entityType, file) {
    this._validateEntityType(entityType);
    if (!file) {
      throw new Error('请先选择文件');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);

    try {
      const response = await httpClient.post(`${this.baseURL}/parse`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      return adaptParseResponse(response.data);
    } catch (error) {
      throw this._handleError(error, '解析失败');
    }
  }

  /**
   * Apply the parsed changes
   * @param {string} entityType
   * @param {Object} changes - parseRoundtrip 返回的 rawChanges（服务端会重新校验）
   * @returns {Promise<{success: Array, failed: Array}>} Normalized apply result
   */
  async applyRoundtrip(entityType, changes) {
    this._validateEntityType(entityType);

    try {
      const response = await httpClient.post(
        `${this.baseURL}/apply`,
        { entity_type: entityType, changes },
        { timeout: 120000 }
      );

      return adaptApplyResponse(response.data);
    } catch (error) {
      throw this._handleError(error, '应用变更失败');
    }
  }

  _validateEntityType(entityType) {
    if (!VALID_ENTITY_TYPES.includes(entityType)) {
      throw new Error(`Invalid entity type: ${entityType}. Must be one of: ${VALID_ENTITY_TYPES.join(', ')}`);
    }
  }

  _extractFilename(response, entityType) {
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      if (contentDisposition.includes('filename*=')) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.*?)(?:;|$)/);
        if (utf8Match && utf8Match[1]) {
          try {
            return decodeURIComponent(utf8Match[1]);
          } catch (e) {
            // fall through to default filename
          }
        }
      }
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        return filenameMatch[1].replace(/['"]/g, '');
      }
    }
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    return `excel_roundtrip_${entityType}_${timestamp}.xlsx`;
  }

  _handleError(error, fallbackMessage) {
    if (error.response) {
      if (error.response.data instanceof Blob) {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              resolve(new Error(errorData.error || errorData.message || fallbackMessage));
            } catch (e) {
              resolve(new Error(fallbackMessage));
            }
          };
          reader.onerror = () => resolve(new Error(fallbackMessage));
          reader.readAsText(error.response.data);
        });
      }
      const message = error.response.data?.error ||
                     error.response.data?.message ||
                     `${fallbackMessage} (${error.response.status})`;
      return new Error(message);
    }
    if (error.code === 'ECONNABORTED') {
      return new Error(`${fallbackMessage}：请求超时，文件可能过大`);
    }
    if (error.message) {
      return error;
    }
    return new Error(fallbackMessage);
  }
}

/**
 * Normalize the parse response into a stable shape for the UI.
 * Centralizes backend field-name mapping — adjust HERE if the backend
 * contract changes, the dialog should not need to change.
 *
 * Backend actual shape (backend/app/services/excel_roundtrip_service.py):
 * data: {
 *   entity_type, total_errors, can_apply,
 *   documents: [{ doc_id, doc_number, status, editable, errors[], warnings[],
 *     modified: [{ row, line_item_id, product_code, product_name, changes: {field: {old, new}} }],
 *     added: [{ row, fields: {product_code, product_name, quantity, unit_price, ...}, errors[] }],
 *     deleted: [{ row, line_item_id, product_code, product_name }] }],
 *   changes: { doc_id: {...} },  // 原样回传给 apply
 * }
 *
 * Output shape:
 * {
 *   documents: [{
 *     doc_id, doc_number, client_name, status, editable, errors[], warnings[],
 *     changes: {
 *       modified: [{ line_item_id, row, fields: [{ field, old, new }] }],
 *       added: [{ row, product_code, name, quantity, unit_price, ... }],
 *       deleted: [{ line_item_id, row, product_code, name }],
 *     },
 *   }],
 *   errors: [string],
 *   rawChanges: object,  // 透传给 applyRoundtrip
 * }
 */
export function adaptParseResponse(raw) {
  const data = raw?.data && typeof raw.data === 'object' ? raw.data : raw;
  const rawDocs = data?.documents || data?.docs || data?.items || [];

  const documents = rawDocs.map((doc) => {
    // 后端把 modified/added/deleted 直接放在单据对象上
    const rawChanges = doc.changes || doc.diff || doc;
    return {
      doc_id: doc.doc_id || doc.document_id || doc.id || doc._id || null,
      doc_number: doc.doc_number || doc.doc_no || doc.number || doc.document_number || '',
      client_name: doc.client_name || doc.client || doc.supplier_name || '',
      status: doc.status || '',
      editable: doc.editable !== false,
      changes: {
        modified: (rawChanges.modified || rawChanges.updated || []).map((m) => ({
          line_item_id: m.line_item_id || m.line_id || m.item_id || null,
          row: m.row ?? m.row_number ?? null,
          fields: normalizeFieldDiffs(m.fields || m.changes),
        })),
        added: (rawChanges.added || rawChanges.new || []).map((a) => {
          const fields = a.fields && typeof a.fields === 'object' ? a.fields : a;
          return {
            ...fields,
            row: a.row ?? a.row_number ?? null,
            product_code: fields.product_code || fields.product_id || fields.code || '',
            name: fields.product_name || fields.name || '',
          };
        }),
        deleted: (rawChanges.deleted || rawChanges.removed || []).map((d) => ({
          line_item_id: d.line_item_id || d.line_id || d.item_id || null,
          row: d.row ?? d.row_number ?? null,
          product_code: d.product_code || d.product_id || d.code || '',
          name: d.product_name || d.name || '',
        })),
      },
      errors: doc.errors || [],
      warnings: doc.warnings || [],
    };
  });

  return {
    documents,
    errors: data?.errors || [],
    rawChanges: data?.changes || null,
  };
}

/**
 * 字段 diff 归一化：后端返回 { field: { old, new } } 的 map，
 * 也兼容数组格式 [{ field, old, new }]，统一输出数组。
 */
function normalizeFieldDiffs(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((f) => ({
      field: f.field || f.name || '',
      old: f.old !== undefined ? f.old : (f.old_value !== undefined ? f.old_value : null),
      new: f.new !== undefined ? f.new : (f.new_value !== undefined ? f.new_value : null),
    }));
  }
  return Object.entries(raw).map(([field, diff]) => ({
    field,
    old: diff && typeof diff === 'object' ? (diff.old ?? null) : null,
    new: diff && typeof diff === 'object' ? (diff.new ?? null) : diff,
  }));
}

/**
 * Normalize the apply response.
 * Backend actual shape: data: { applied: [{doc_id, doc_number, ...}],
 *   failed: [{doc_id, doc_number, errors: [...]}], skipped: [...],
 *   applied_count, failed_count, warnings }
 * Output shape: { success: [{ doc_id, doc_number }], failed: [{ doc_id, doc_number, reason }] }
 */
export function adaptApplyResponse(raw) {
  const data = raw?.data && typeof raw.data === 'object' ? raw.data : raw;
  return {
    success: (data?.applied || data?.success || data?.succeeded || []).map((s) => (
      typeof s === 'string' ? { doc_id: s, doc_number: s } : {
        doc_id: s.doc_id || s.document_id || s.id || null,
        doc_number: s.doc_number || s.doc_no || s.number || '',
      }
    )),
    failed: (data?.failed || data?.errors_failed || []).map((f) => ({
      doc_id: f.doc_id || f.document_id || f.id || null,
      doc_number: f.doc_number || f.doc_no || f.number || '',
      reason: f.reason || f.error ||
        (Array.isArray(f.errors) ? f.errors.join('；') : f.errors) ||
        f.message || '未知原因',
    })),
  };
}

// Create and export singleton instance
const excelRoundtripService = new ExcelRoundtripService();
export default excelRoundtripService;
